import React, { useEffect, useRef, useState } from 'react';
import Layout from '../components/layout/Layout';
import { useFamilyStore } from '../stores/familyStore';
import { useAuthStore } from '../stores/authStore';
import { FamilyMembersList } from '../components/family/FamilyMembersList';
import { ChildrenList } from '../components/family/ChildrenList';
import { cn } from '../utils/cn';
import { colorSchemes, getColorScheme } from '../utils/colorSchemes';
import logger from '../utils/logger';
import { useUserPreferencesStore } from '../stores/userPreferencesStore';
import { usePermissions } from '../hooks/usePermissions';
import { getSettingsTabAccess } from '../utils/accessControl';
import { usePWAInstall } from '../hooks/usePWAInstall';
import { EncryptionMigration } from '../components/settings/EncryptionMigration';

/**
 * Family settings page with access control - Mobile First Design
 */
type SettingsTab = 'family' | 'members' | 'install' | 'appearance';

export const FamilySettings: React.FC = () => {
  const { user } = useAuthStore();
  const { family, isLoading, error, initialize, updateFamily, createFamily } = useFamilyStore();
  const { preferences, updateColorScheme, updatePreferredCurrency, loadPreferences, isLoading: preferencesLoading } = useUserPreferencesStore();
  const {
    userRole,
    canEditFamilySettings
  } = usePermissions();

  const [activeTab, setActiveTab] = useState<SettingsTab>(() => {
    // Get saved tab from sessionStorage
    const savedTab = sessionStorage.getItem('family-settings-active-tab');
    // If saved tab was old 'children', switch to 'family'
    if (savedTab === 'children') return 'family';
    return (savedTab as SettingsTab) || 'family';
  });
  const [familyName, setFamilyName] = useState('');
  const [homeAName, setHomeAName] = useState('');
  const [homeBName, setHomeBName] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  // PWA Install Hook
  const { isInstallable, isInstalled, promptInstall } = usePWAInstall();

  // Add flag to prevent multiple initializations
  const initRef = useRef(false);

  useEffect(() => {
    if (user && !family && !isLoading && !initRef.current) {
      initRef.current = true;
      initialize();
    }
  }, [user, family, isLoading, initialize]);

  // Update local states when family loads
  useEffect(() => {
    if (family) {
      setFamilyName(family.name);
      setHomeAName(family.home_a_name || 'Home A');
      setHomeBName(family.home_b_name || 'Home B');
    }
  }, [family]);

  // Load user preferences
  useEffect(() => {
    if (user) {
      loadPreferences();
    }
  }, [user, loadPreferences]);

  // Check tab access
  const tabAccess = getSettingsTabAccess(userRole);

  // Save activeTab to sessionStorage whenever it changes
  useEffect(() => {
    sessionStorage.setItem('family-settings-active-tab', activeTab);
  }, [activeTab]);

  // Don't redirect to create family - handle creation in General tab

  const allTabs = [
    { id: 'family', label: 'General', icon: '🏠', access: 'family' },
    { id: 'members', label: 'Access', icon: '👥', access: 'members' },
    { id: 'install', label: 'Install', icon: '📱', access: 'install' },
    { id: 'appearance', label: 'Appearance', icon: '🎨', access: 'appearance' }
  ];

  // Filter tabs based on permissions
  const tabs = allTabs.filter(tab => tabAccess[tab.access as keyof typeof tabAccess]);

  const handleSaveFamilySettings = async () => {
    setIsSaving(true);
    try {
      if (!family) {
        if (!familyName || !familyName.trim()) {
          alert('Enter family name to create it');
          return;
        }
        const familyId = await createFamily(familyName.trim());
        if (!familyId) return;
        // After creation, update home names if different from default
        // Use defaults if empty to prevent saving empty strings
        if (homeAName || homeBName) {
          await updateFamily({
            home_a_name: homeAName || 'Home A',
            home_b_name: homeBName || 'Home B'
          });
        }
      } else {
        await updateFamily({
          name: familyName,
          // Use defaults if empty to prevent saving empty strings
          home_a_name: homeAName || 'Home A',
          home_b_name: homeBName || 'Home B'
        });
      }
    } finally {
      setIsSaving(false);
    }
  };

  // Removed password change from Settings - available in /account

  if (isLoading) {
    return (
      <Layout>
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center">
            <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600">Loading settings...</p>
          </div>
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div className="min-h-screen bg-gray-50 p-4">
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg">
            <p>Error occurred: {error}</p>
          </div>
        </div>
      </Layout>
    );
  }

  // Production: allow entering settings even without existing family

  return (
    <Layout>
      <div className="bg-gray-50">
        {/* Mobile-first tab navigation - Sticky */}
        <div className="bg-white border-b sticky top-0 z-10">
          <div className="px-4 py-2">
            <div className="grid grid-cols-4 gap-1">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as SettingsTab)}
                  className={cn(
                    "flex flex-col items-center justify-center p-2 rounded-lg cursor-pointer select-none",
                    "transition-colors duration-100 active:scale-95",
                    "text-xs font-medium touch-manipulation",
                    activeTab === tab.id
                      ? "bg-blue-100 text-blue-600 border-2 border-blue-200"
                      : "text-gray-600 hover:bg-gray-100 border-2 border-transparent"
                  )}
                >
                  <span className="text-lg mb-1">{tab.icon}</span>
                  <span className="leading-tight text-center">{tab.label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Tab Content */}
        <div className="p-3">
          {/* Tab: General Settings */}
          {activeTab === 'family' && (
            <div className="bg-white rounded-lg shadow-sm border">
              <div className="p-3 border-b bg-gray-50">
                <h2 className="text-lg font-semibold text-gray-800 flex items-center">
                  <span className="mr-2">🏠</span>
                  General Settings
                </h2>
              </div>

              <div className="p-3 space-y-3">
                <div>
                  <h3 className="text-base font-medium text-gray-700 mb-2 flex items-center">
                    <span className="mr-2">🏡</span>
                    Home / Caregiver names
                  </h3>
                  <div className="grid grid-cols-2 gap-2">
                    {(() => {
                      const scheme = getColorScheme(preferences.color_scheme);
                      return (
                        <>
                          {/* Home A */}
                          <div className={`p-2 rounded-lg bg-gradient-to-r ${scheme.homeA.bg} border ${scheme.homeA.border}`}>
                            <h4 className={`text-base font-medium ${scheme.homeA.text} flex items-center mb-1`}>
                              <div className="w-3 h-3 rounded-full mr-1" style={{ backgroundColor: scheme.homeA.color }}></div>
                              Home A
                            </h4>
                            <input
                              type="text"
                              placeholder="Dad's Home"
                              value={homeAName}
                              onChange={canEditFamilySettings ? (e) => setHomeAName(e.target.value) : undefined}
                              disabled={!canEditFamilySettings}
                              className={cn(
                                "w-full text-base rounded-lg px-2 py-1",
                                canEditFamilySettings
                                  ? `bg-white/80 border ${scheme.homeA.border} focus:outline-none focus:ring-1 focus:ring-blue-500`
                                  : "bg-gray-50 border-gray-200 text-gray-500 cursor-not-allowed"
                              )}
                            />
                          </div>

                          {/* Home B */}
                          <div className={`p-2 rounded-lg bg-gradient-to-r ${scheme.homeB.bg} border ${scheme.homeB.border}`}>
                            <h4 className={`text-base font-medium ${scheme.homeB.text} flex items-center mb-1`}>
                              <div className="w-3 h-3 rounded-full mr-1" style={{ backgroundColor: scheme.homeB.color }}></div>
                              Home B
                            </h4>
                            <input
                              type="text"
                              placeholder="Mom's Home"
                              value={homeBName}
                              onChange={canEditFamilySettings ? (e) => setHomeBName(e.target.value) : undefined}
                              disabled={!canEditFamilySettings}
                              className={cn(
                                "w-full text-base rounded-lg px-2 py-1",
                                canEditFamilySettings
                                  ? `bg-white/80 border ${scheme.homeB.border} focus:outline-none focus:ring-1 focus:ring-blue-500`
                                  : "bg-gray-50 border-gray-200 text-gray-500 cursor-not-allowed"
                              )}
                            />
                          </div>
                        </>
                      );
                    })()}
                  </div>
                </div>

                {/* Save changes button - only for home and family name */}
                <div className="flex justify-end pt-2 border-t mt-3">
                  {canEditFamilySettings && (
                    <button
                      onClick={handleSaveFamilySettings}
                      disabled={isSaving}
                      className="btn"
                    >
                      {isSaving ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                          Saving...
                        </>
                      ) : (
                        <>Save changes</>
                      )}
                    </button>
                  )}
                  {!canEditFamilySettings && (
                    <div className="text-xs text-gray-500 italic">
                      Only admin can edit family settings
                    </div>
                  )}
                </div>

                {/* Children moved to General */}
                <div className="pt-2">
                  <h3 className="text-base font-medium text-gray-700 mb-2 flex items-center">
                    <span className="mr-2">👫</span>
                    Children / Dependents
                  </h3>
                  {family ? (
                    <div className="border rounded-lg p-2">
                      <ChildrenList />
                    </div>
                  ) : (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-2 text-xs text-yellow-800">
                      Create a family to add children.
                    </div>
                  )}
                </div>

                {/* Preferred currency */}
                <div className="pt-3">
                  <h3 className="text-base font-medium text-gray-700 mb-2 flex items-center">
                    <span className="mr-2">💱</span>
                    Preferred currency for expenses
                  </h3>
                  <div className="inline-flex items-center bg-gray-100 rounded-lg p-1">
                    <button
                      type="button"
                      onClick={async () => { await updatePreferredCurrency('USD'); }}
                      disabled={preferencesLoading}
                      className={cn(
                        "px-4 py-2 text-sm rounded-md transition",
                        preferences.preferred_currency === 'USD'
                          ? "bg-white shadow text-blue-600 font-medium"
                          : "text-gray-600 hover:bg-gray-200"
                      )}
                    >
                      USD ($)
                    </button>
                    <button
                      type="button"
                      onClick={async () => { await updatePreferredCurrency('EUR'); }}
                      disabled={preferencesLoading}
                      className={cn(
                        "px-4 py-2 text-sm rounded-md transition",
                        preferences.preferred_currency === 'EUR'
                          ? "bg-white shadow text-blue-600 font-medium"
                          : "text-gray-600 hover:bg-gray-200"
                      )}
                    >
                      EUR (€)
                    </button>
                  </div>
                  <p className="text-[11px] text-gray-500 mt-1">
                    This currency will be selected by default when adding expenses. You can still change it per expense.
                  </p>
                </div>

                {/* Encryption section */}
                <div className="pt-3">
                  <h3 className="text-base font-medium text-gray-700 mb-2 flex items-center">
                    <span className="mr-2">&#x1F512;</span>
                    Data encryption (E2EE)
                  </h3>
                  <EncryptionMigration />
                </div>
              </div>
            </div>
          )}

          {/* Tab: Install */}
          {activeTab === 'install' && (
            <div className="bg-white rounded-lg shadow-sm border">
              <div className="p-3 border-b bg-gray-50">
                <h2 className="text-base font-semibold text-gray-800 flex items-center">
                  <span className="mr-2">📱</span>
                  Install 2homes.app
                </h2>
              </div>
              <div className="p-3">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-3">
                  <div>
                    <p className="text-xs text-blue-700">
                      Install the app for faster access to the calendar without the browser address bar.
                    </p>
                  </div>
                  <div className="mt-3">
                    <button
                      onClick={promptInstall}
                      disabled={!isInstallable || isInstalled}
                      className={cn(
                        "w-full py-2 px-4 rounded-lg text-sm font-medium flex items-center justify-center",
                        isInstallable && !isInstalled
                          ? "bg-blue-600 hover:bg-blue-700 text-white"
                          : "bg-gray-300 text-gray-500 cursor-not-allowed"
                      )}
                    >
                      {isInstalled ? (
                        <>
                          <span className="mr-2">✅</span>
                          App already installed
                        </>
                      ) : isInstallable ? (
                        <>
                          <span className="mr-2">📲</span>
                          Install (Android)
                        </>
                      ) : (
                        <>
                          <span className="mr-2">⏳</span>
                          Installation unavailable
                        </>
                      )}
                    </button>
                    <div className="text-xs text-blue-700 mt-2 text-center">
                      iOS: Safari → Share → 'Add to Home Screen'.
                    </div>
                    {!isInstallable && !isInstalled && (
                      <p className="text-xs text-blue-600 mt-2 text-center">
                        To install the app, open it in Chrome or Safari.
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Tab: Access */}
          {activeTab === 'members' && (
            <div className="bg-white rounded-lg shadow-sm border">
              <div className="p-2 border-b bg-gray-50">
                <h2 className="text-base font-semibold text-gray-800 flex items-center">
                  <span className="mr-2">👥</span>
                  Access
                </h2>
              </div>
              <div className="p-2">
                <FamilyMembersList />
              </div>
            </div>
          )}

          {/* Tab: Appearance */}
          {activeTab === 'appearance' && (
            <div className="bg-white rounded-lg shadow-sm border">
              <div className="p-2 border-b bg-gray-50">
                <h2 className="text-base font-semibold text-gray-800 flex items-center">
                  <span className="mr-2">🎨</span>
                  Color scheme
                </h2>
              </div>

              <div className="p-2">
                <div className="grid grid-cols-1 gap-2">
                  {Object.values(colorSchemes).map((scheme) => (
                    <div
                      key={scheme.id}
                      onClick={async () => {
                        const success = await updateColorScheme(scheme.id);
                        if (success) {
                          logger.debug(`✅ Scheme ${scheme.id} applied`);
                        }
                      }}
                      className={`p-2 border-2 rounded-lg cursor-pointer select-none touch-manipulation transition-colors duration-100 hover:shadow-md active:scale-98 ${preferences.color_scheme === scheme.id
                        ? 'border-blue-500 bg-blue-50 shadow-md'
                        : 'border-gray-200 hover:border-gray-300'
                        }`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <h4 className="font-medium text-sm">{scheme.name}</h4>
                        {preferences.color_scheme === scheme.id && (
                          <div className="w-4 h-4 bg-blue-500 rounded-full flex items-center justify-center">
                            <span className="text-white text-xs">✓</span>
                          </div>
                        )}
                      </div>

                      <div className="flex space-x-2">
                        {/* Preview Home A */}
                        <div className={`flex-1 p-2 rounded-lg bg-gradient-to-r ${scheme.homeA.bg} border ${scheme.homeA.border}`}>
                          <div className={`text-xs ${scheme.homeA.text} font-medium flex items-center`}>
                            <div className="w-3 h-3 rounded-full mr-1" style={{ backgroundColor: scheme.homeA.color }}></div>
                            Home A
                          </div>
                        </div>

                        {/* Preview Home B */}
                        <div className={`flex-1 p-2 rounded-lg bg-gradient-to-r ${scheme.homeB.bg} border ${scheme.homeB.border}`}>
                          <div className={`text-xs ${scheme.homeB.text} font-medium flex items-center`}>
                            <div className="w-3 h-3 rounded-full mr-1" style={{ backgroundColor: scheme.homeB.color }}></div>
                            Home B
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {preferencesLoading && (
                  <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded-lg flex items-center justify-center">
                    <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mr-2"></div>
                    <span className="text-blue-700 text-xs font-medium">Saving...</span>
                  </div>
                )}

                <div className="mt-2 p-2 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-600 text-center">
                    ✨ Scheme is automatically saved upon selection
                  </p>
                </div>
              </div>
            </div>
          )}


        </div>
      </div>
    </Layout>
  );
};
